/**
 * CheckInHistory Component
 *
 * Displays a timeline of check-ins for a key result.
 */

import React from 'react';
import clsx from 'clsx';
import { ClockIcon, UserIcon } from '@heroicons/react/24/outline';
import type { CheckInHistoryProps, CheckInHistoryItemProps } from '../checkIns.types';
import { formatCheckInDate } from '../checkIns.definitions';

function CheckInHistoryItem({ checkIn, isFirst, isLast }: CheckInHistoryItemProps) {

  return (
    <div className="relative flex gap-4 pb-8">
      {/* Timeline Line */}
      {!isLast && (
        <div className="absolute left-[15px] top-8 h-full w-0.5 bg-gray-200" />
      )}

      {/* Timeline Dot */}
      <div
        className={clsx(
          'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2',
          isFirst
            ? 'border-blue-500 bg-blue-100'
            : 'border-gray-300 bg-white'
        )}
      >
        <div
          className={clsx(
            'h-3 w-3 rounded-full',
            isFirst ? 'bg-blue-500' : 'bg-gray-400'
          )}
        />
      </div>

      {/* Content */}
      <div className="flex-1 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        {/* Header */}
        <div className="mb-2 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <ClockIcon className="h-4 w-4" />
              <span className="font-montserrat font-medium">
                {formatCheckInDate(checkIn.checkInDate.toDate())}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
              <UserIcon className="h-3 w-3" />
              <span className="font-montserrat">{checkIn.recordedByName}</span>
            </div>
          </div>

          {/* Progress Change */}
          <div className="text-right">
            <div className="font-montserrat text-lg font-bold text-gray-900">
              {checkIn.previousValue} → {checkIn.newValue}
            </div>
            <div className="text-xs text-gray-600">
              Progresso: +{checkIn.progressChange}%
            </div>
          </div>
        </div>

        {/* Notes */}
        {checkIn.notes && (
          <div className="mb-2">
            <p className="text-sm text-gray-700">{checkIn.notes}</p>
          </div>
        )}

        {/* Impediments */}
        {checkIn.impediments && (
          <div className="mb-2 rounded-lg bg-yellow-50 p-3">
            <p className="mb-1 font-montserrat text-xs font-semibold uppercase text-yellow-800">
              Impedimentos
            </p>
            <p className="text-sm text-yellow-900">{checkIn.impediments}</p>
          </div>
        )}

        {/* Actions Required */}
        {checkIn.actionsRequired && (
          <div className="rounded-lg bg-blue-50 p-3">
            <p className="mb-1 font-montserrat text-xs font-semibold uppercase text-blue-800">
              Ações Necessárias
            </p>
            <p className="text-sm text-blue-900">{checkIn.actionsRequired}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CheckInHistory({ checkIns, keyResult, className }: CheckInHistoryProps) {
  if (checkIns.length === 0) {
    return (
      <div className={clsx('rounded-lg border-2 border-dashed border-gray-300 p-8 text-center', className)}>
        <p className="font-montserrat text-gray-500">
          Nenhum check-in registrado ainda.
        </p>
        <p className="mt-1 text-sm text-gray-400">
          Registre o primeiro check-in para acompanhar o progresso.
        </p>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-0', className)}>
      {/* Key Result Info (if provided) */}
      {keyResult && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="font-montserrat text-lg font-semibold text-gray-900">
            {keyResult.title}
          </h3>
          <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
            <span>
              Meta: {keyResult.initialValue} → {keyResult.targetValue} {keyResult.unit}
            </span>
            <span>•</span>
            <span>Atual: {keyResult.currentValue} {keyResult.unit}</span>
            <span>•</span>
            <span>Progresso: {keyResult.progress}%</span>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="relative">
        {checkIns.map((checkIn, index) => (
          <CheckInHistoryItem
            key={checkIn.id}
            checkIn={checkIn}
            isFirst={index === 0}
            isLast={index === checkIns.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
